import React, { useCallback, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { v7 } from 'uuid';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import PratusInput from 'components/DesignSystems/New/PratusInput';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';

export type TextFieldDescriptor = {
  type: 'text';
  accessorKey: string;
  header: string;
  placeholder?: string;
  sortable?: boolean;
  maxLength?: number;
};

export type BooleanFieldDescriptor = {
  type: 'boolean';
  accessorKey: string;
  header: string;
  ariaLabel: string;
};

export type GroupedBooleanFieldDescriptor = {
  type: 'grouped-boolean';
  header: string;
  mode?: 'checkbox' | 'radio';
  options: Array<{
    accessorKey: string;
    label: string;
    ariaLabel: string;
  }>;
};

export type FieldDescriptor =
  | TextFieldDescriptor
  | BooleanFieldDescriptor
  | GroupedBooleanFieldDescriptor;

type InternalDraftRow = {
  _isDraft: true;
  _draftId: string;
} & Record<string, string | boolean>;

type InternalRow<TEntity> = TEntity | InternalDraftRow;

const isDraft = <TEntity,>(
  row: InternalRow<TEntity>
): row is InternalDraftRow =>
  typeof row === 'object' && row !== null && '_isDraft' in row;

export type InlineEntityTableProps<TEntity extends { id: string }> = {
  title: string;
  addButtonLabel: string;
  noDataMessage: string;
  entities: TEntity[];
  emptyDraft: Record<string, string | boolean>;
  requiredField: string;
  fields: FieldDescriptor[];
  isCreating: boolean;
  onSaveDraft: (
    draft: Record<string, string | boolean>,
    onSuccess: () => void
  ) => void;
  onSaveField: (
    entity: TEntity,
    field: string,
    value: string | boolean
  ) => void;
  onSaveFields?: (
    entity: TEntity,
    updates: Record<string, string | boolean>
  ) => void;
  onDelete?: (entity: TEntity) => void;
  readOnly?: boolean;
};

export function MedicalPlanTable<TEntity extends { id: string }>({
  title,
  addButtonLabel,
  noDataMessage,
  entities,
  emptyDraft,
  requiredField,
  fields,
  isCreating,
  onSaveDraft,
  onSaveField,
  onSaveFields,
  onDelete,
  readOnly = false,
}: InlineEntityTableProps<TEntity>) {
  const [draftRows, setDraftRows] = useState<InternalDraftRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const addDraft = useCallback(() => {
    setDraftRows((prev) => [
      ...prev,
      { _isDraft: true, _draftId: v7(), ...emptyDraft },
    ]);
  }, [emptyDraft]);

  const updateDraft = useCallback(
    (draftId: string, key: string, value: string | boolean) => {
      setDraftRows((prev) =>
        prev.map((d) => (d._draftId === draftId ? { ...d, [key]: value } : d))
      );
    },
    []
  );

  const removeDraft = useCallback((draftId: string) => {
    setDraftRows((prev) => prev.filter((d) => d._draftId !== draftId));
  }, []);

  const textFields = useMemo(
    () => fields.filter((f): f is TextFieldDescriptor => f.type === 'text'),
    [fields]
  );

  const filteredEntities = useMemo(() => {
    if (!searchTerm.trim()) return entities;
    const lower = searchTerm.toLowerCase();
    return entities.filter((entity) =>
      textFields.some((f) => {
        const val = (entity as Record<string, unknown>)[f.accessorKey];
        return typeof val === 'string' && val.toLowerCase().includes(lower);
      })
    );
  }, [entities, searchTerm, textFields]);

  const tableData = useMemo<InternalRow<TEntity>[]>(
    () => [...filteredEntities, ...draftRows],
    [filteredEntities, draftRows]
  );

  const columns = useMemo<ColumnDef<InternalRow<TEntity>>[]>(
    () => [
      ...fields.map((field): ColumnDef<InternalRow<TEntity>> => {
        if (field.type === 'grouped-boolean') {
          const isRadio = field.mode === 'radio';
          const columnId = field.options.map((o) => o.accessorKey).join('-');
          return {
            id: columnId,
            header: field.header,
            enableSorting: false,
            cell: ({ row }) => {
              const original = row.original;
              const rowId = isDraft(original)
                ? original._draftId
                : (original as TEntity).id;
              return (
                <div className="d-flex flex-column gap-1">
                  {field.options.map((opt) => {
                    const checked =
                      ((original as Record<string, unknown>)[
                        opt.accessorKey
                      ] as boolean) ?? false;

                    const handleChange = () => {
                      if (readOnly) return;
                      if (isRadio) {
                        const updates = Object.fromEntries(
                          field.options.map((o) => [
                            o.accessorKey,
                            o.accessorKey === opt.accessorKey,
                          ])
                        );
                        if (isDraft(original)) {
                          Object.entries(updates).forEach(([key, val]) =>
                            updateDraft(original._draftId, key, val)
                          );
                        } else {
                          onSaveFields?.(original as TEntity, updates);
                        }
                      } else {
                        if (isDraft(original)) {
                          updateDraft(
                            original._draftId,
                            opt.accessorKey,
                            !checked
                          );
                        } else {
                          onSaveField(
                            original as TEntity,
                            opt.accessorKey,
                            !checked
                          );
                        }
                      }
                    };

                    return (
                      <label key={opt.accessorKey} className="form-check mb-0">
                        <input
                          type={isRadio ? 'radio' : 'checkbox'}
                          className="form-check-input"
                          name={isRadio ? `${rowId}-${columnId}` : undefined}
                          aria-label={opt.ariaLabel}
                          checked={checked}
                          disabled={readOnly}
                          onChange={handleChange}
                        />
                        <span className="form-check-label">{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
              );
            },
          };
        }

        return {
          accessorKey: field.accessorKey,
          header: field.header,
          enableSorting:
            field.type === 'text' ? (field.sortable ?? false) : false,
          cell:
            field.type === 'text'
              ? ({ row, getValue }) => {
                  const original = row.original;
                  if (isDraft(original)) {
                    const draftValue =
                      (original[field.accessorKey] as string) ?? '';
                    return (
                      <div>
                        <input
                          className="form-control form-control-sm"
                          value={draftValue}
                          onChange={(e) =>
                            updateDraft(
                              original._draftId,
                              field.accessorKey,
                              e.target.value
                            )
                          }
                          maxLength={field.maxLength}
                          placeholder={field.placeholder}
                        />
                        {field.maxLength !== undefined && (
                          <div className="form-text text-muted mt-1">
                            {draftValue.length} / {field.maxLength}
                          </div>
                        )}
                      </div>
                    );
                  }
                  if (readOnly) {
                    return <span>{(getValue() as string) ?? ''}</span>;
                  }
                  return (
                    <PratusInput
                      value={(getValue() as string) ?? ''}
                      onSave={(value) =>
                        onSaveField(
                          original as TEntity,
                          field.accessorKey,
                          value
                        )
                      }
                      placeholder={field.placeholder}
                      maxLength={field.maxLength}
                      showErrorContainer={false}
                    />
                  );
                }
              : ({ row, getValue }) => {
                  const original = row.original;
                  return (
                    <input
                      type="checkbox"
                      className="form-check-input"
                      aria-label={(field as BooleanFieldDescriptor).ariaLabel}
                      checked={(getValue() as boolean) ?? false}
                      disabled={readOnly}
                      onChange={(e) => {
                        if (readOnly) return;
                        if (isDraft(original)) {
                          updateDraft(
                            original._draftId,
                            field.accessorKey,
                            e.target.checked
                          );
                        } else {
                          onSaveField(
                            original as TEntity,
                            field.accessorKey,
                            e.target.checked
                          );
                        }
                      }}
                    />
                  );
                },
        };
      }),
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const original = row.original;
          if (isDraft(original)) {
            return (
              <div className="d-flex gap-1">
                <StylishNewButton
                  className="button--primary"
                  disabled={!original[requiredField] || isCreating}
                  onClick={() => {
                    const { _isDraft: _d, _draftId, ...draftData } = original;
                    onSaveDraft(draftData, () => removeDraft(_draftId));
                  }}
                >
                  <SharedIcon iconName="check" />
                </StylishNewButton>
                <StylishNewButton
                  className="button--secondary"
                  onClick={() => removeDraft(original._draftId)}
                >
                  <SharedIcon iconName="close" />
                </StylishNewButton>
              </div>
            );
          }
          if (!readOnly && onDelete) {
            return (
              <StylishNewButton
                className="button--tertiary"
                aria-label="Delete record"
                onClick={() => onDelete(original as TEntity)}
              >
                <SharedIcon iconName="delete" />
              </StylishNewButton>
            );
          }
          return null;
        },
      },
    ],
    [
      fields,
      readOnly,
      updateDraft,
      removeDraft,
      onSaveDraft,
      onSaveField,
      onSaveFields,
      onDelete,
      isCreating,
      requiredField,
    ]
  );

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2 gap-3">
        <h5 className="mt-0 mb-0 flex-shrink-0">{title}</h5>
        <input
          className="form-control"
          style={{ minWidth: 200 }}
          placeholder={`Search ${title.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {!readOnly && (
          <StylishNewButton
            className="button button--primary flex-shrink-0"
            onClick={addDraft}
          >
            <SharedIcon iconName="add" classes="me-2" />
            {addButtonLabel}
          </StylishNewButton>
        )}
      </div>
      <PratusTable
        className="table-sm medical-plan-table"
        headerClassName="table-dark"
        columns={columns}
        data={tableData}
        getRowId={(row) => (isDraft(row) ? row._draftId : (row as TEntity).id)}
        noDataIndication={<span>{noDataMessage}</span>}
      />
    </div>
  );
}
